import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import pLimit from "p-limit";

const EventContext = createContext();

export function useEventContext() {
  return useContext(EventContext);
}

export function EventProvider({ children }) {
  const [list, setList] = useState([]);
  const [priceDetails, setPriceDetails] = useState({});
  const [ticketData, setTicketData] = useState({});
  const [customItems, setCustomItems] = useState({});
  const [studentPrices, setStudentPrices] = useState({});
  const [generalAdmTicketTypeId, setGeneralAdmTicketTypeIds] = useState({});
  const [studentTicketTypeId, setStudentTicketTypeId] = useState({});
  const [ticketTypeNames, setTicketTypeNames] = useState({});
  const [eventInstances, setEventInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableStatus, setAvailableStatus] = useState([]);

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const baseUrl = "https://events.jazzforumarts.org/jazzforumarts/api/v3";
        const todayDate = new Date(new Date().toISOString().substring(0, 10));
        const eventsResponse = await fetch(`${baseUrl}/events?onSale=true`);
        const events = await eventsResponse.json();

        const relevantEvents = events.filter(
          (e) =>
            !e.attribute_HideEvent &&
            e.attribute_EventCategory === "Summer Concerts: Dobbs Ferry"
        );

        setList(relevantEvents);
        const eventIds = relevantEvents.map((e) => e.id);

        const limit = pLimit(10);
        const instanceFetches = eventIds.map((id) =>
          limit(() =>
            fetch(`${baseUrl}/events/${id}/instances`).then((res) => res.json())
          )
        );
        const instanceResults = await Promise.allSettled(instanceFetches);

        const updatedEventInstances = {};
        const instanceIds = [];
        instanceResults.forEach((result, index) => {
          if (result.status === "fulfilled" && Array.isArray(result.value)) {
            const futureInstances = result.value.filter(
              (inst) =>
                inst.isOnSale && inst.start && new Date(inst.start) >= todayDate
            );
            if (futureInstances.length) {
              updatedEventInstances[eventIds[index]] = futureInstances;
              futureInstances.forEach((inst) => instanceIds.push(inst.id));
            }
          } else {
            console.error(
              `Failed fetching instances for event ${eventIds[index]}`
            );
          }
        });
        setEventInstances(updatedEventInstances);

        const priceFetches = instanceIds.map((id) =>
          limit(() =>
            fetch(
              `${baseUrl}/instances/${id}/price-list?$expand=prices/customItems`
            )
              .then((res) => res.json())
              .then((data) => ({ id, data }))
          )
        );
        const availFetches = instanceIds.map((id) =>
          limit(() =>
            fetch(
              `${baseUrl}/instances/${id}/status?includeLockInformation=true&includeChildPlans=true`
            )
              .then((res) => res.json())
              .then((data) => ({ id, data }))
          )
        );

        const [priceResults, availResults] = await Promise.all([
          Promise.allSettled(priceFetches),
          Promise.allSettled(availFetches),
        ]);

        const updatedEventPrices = {},
          updatedCustomItems = {},
          updatedStudentPrices = {},
          updatedAdmTicketTypeIds = {},
          updatedStuTicketTypeIds = {},
          updatedTicketTypeNames = {},
          updatedTicketData = {},
          updatedAvailableStatus = {};

        priceResults.forEach((res) => {
          if (res.status === "fulfilled" && res.value.data?.prices) {
            const { id, data } = res.value;
            const std = data.prices.find((p) => p.isBandDefault);
            if (std) {
              updatedEventPrices[id] = std.amount;
              updatedCustomItems[id] = std.customItems.map((c) => c.amount);
              updatedTicketTypeNames[id] = std.ticketType.name;
            }
            const stu = data.prices.find(
              (p) => p.ticketType.name === "Student (must present ID at door)"
            );
            if (stu) {
              updatedStudentPrices[id] = stu.amount;
              updatedStuTicketTypeIds[id] = stu.ticketType.id;
            }
            const gen = data.prices.find(
              (p) => p.ticketType.name === "General Adm"
            );
            if (gen) {
              updatedAdmTicketTypeIds[id] = gen.ticketType.id;
            }
            updatedTicketData[id] = data.prices.map((p) => ({
              ticketTypeName: p.ticketType.name,
              ticketTypeId: p.ticketType.id,
              ticketAmount: p.amount,
              commissionAmount: p.customItems[0]?.amount,
            }));
          }
        });

        availResults.forEach((res) => {
          if (res.status === "fulfilled") {
            updatedAvailableStatus[res.value.id] =
              res.value.data?.available ?? 0;
          }
        });

        setPriceDetails(updatedEventPrices);
        setCustomItems(updatedCustomItems);
        setStudentPrices(updatedStudentPrices);
        setGeneralAdmTicketTypeIds(updatedAdmTicketTypeIds);
        setStudentTicketTypeId(updatedStuTicketTypeIds);
        setTicketTypeNames(updatedTicketTypeNames);
        setTicketData(updatedTicketData);
        setAvailableStatus(updatedAvailableStatus);
      } catch (err) {
        console.error("Error in data fetching pipeline", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const mergedEventDetails = useMemo(() => {
    return Object.entries(eventInstances)
      .map(([eventId, instances]) => {
        const event = list.find((e) => e.id === eventId);
        return event
          ? {
              event,
              eventInstances: instances,
              priceDetails,
              customItems,
              studentPrices,
              generalAdmTicketTypeId,
              studentTicketTypeId,
              ticketTypeNames,
              ticketData,
              availableStatus,
            }
          : null;
      })
      .filter(Boolean);
  }, [
    eventInstances,
    list,
    priceDetails,
    customItems,
    studentPrices,
    generalAdmTicketTypeId,
    studentTicketTypeId,
    ticketTypeNames,
    ticketData,
    availableStatus,
  ]);

  const sortedMergedEventDetails = useMemo(() => {
    return [...mergedEventDetails].sort((a, b) => {
      const startA = new Date(a.eventInstances[0]?.start || 0);
      const startB = new Date(b.eventInstances[0]?.start || 0);
      return startA - startB;
    });
  }, [mergedEventDetails]);

  const transformedEventsTwo = useMemo(() => {
    return sortedMergedEventDetails.map(
      ({
        event,
        eventInstances,
        priceDetails,
        customItems,
        studentPrices,
        studentTicketTypeId,
        generalAdmTicketTypeId,
        ticketTypeNames,
        ticketData,
        availableStatus,
      }) => ({
        ...eventInstances,
        eventDetails: event,
        priceDetails,
        customItems,
        studentPrices,
        studentTicketTypeId,
        generalAdmTicketTypeId,
        ticketTypeNames,
        ticketData,
        availableStatus,
      })
    );
  }, [sortedMergedEventDetails]);

  const { customEventDetails, pastCustomEventDetails } = useMemo(() => {
    const customEventDetails = [];
    const pastCustomEventDetails = [];

    transformedEventsTwo.forEach((instance) => {
      const keys = Object.keys(instance);
      const hasValidInstances = keys.some(
        (k) => k !== "eventDetails" && instance[k]?.start
      );
      if (hasValidInstances) {
        keys.forEach((k) => {
          if (k !== "eventDetails" && instance[k]?.start) {
            customEventDetails.push({
              start: instance[k].start,
              isOnSale: instance[k].isOnSale,
              planId: instance[k].planId,
              instanceId: instance[k].id,
              name: instance.eventDetails.name,
              id: instance.eventDetails.id,
              ...instance.eventDetails,
              prices: priceDetails[instance[k].id],
              commission: customItems[instance[k].id],
              studentPrice: studentPrices[instance[k].id],
              studentTicketTypeId: studentTicketTypeId[instance[k].id],
              generalTicketTypeId: generalAdmTicketTypeId[instance[k].id],
              ticketTypeName: ticketTypeNames[instance[k].id],
              ticketData: ticketData[instance[k].id],
              availableStatus: availableStatus[instance[k].id],
            });
          }
        });
      } else {
        pastCustomEventDetails.push(instance.eventDetails);
      }
    });

    return { customEventDetails, pastCustomEventDetails };
  }, [
    transformedEventsTwo,
    priceDetails,
    customItems,
    studentPrices,
    studentTicketTypeId,
    generalAdmTicketTypeId,
    ticketTypeNames,
    ticketData,
    availableStatus,
  ]);

  return (
    <EventContext.Provider
      value={{
        list,
        setList,
        priceDetails,
        setPriceDetails,
        ticketData,
        setTicketData,
        customItems,
        setCustomItems,
        studentPrices,
        setStudentPrices,
        generalAdmTicketTypeId,
        setGeneralAdmTicketTypeIds,
        studentTicketTypeId,
        setStudentTicketTypeId,
        ticketTypeNames,
        setTicketTypeNames,
        availableStatus,
        setAvailableStatus,
        customEventDetails,
        pastCustomEventDetails,
        eventInstances,
        loading,
      }}
    >
      {children}
    </EventContext.Provider>
  );
}